import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ScrollArea } from './ui/scroll-area';
import {
  Inbox, Send, FileText, AlertCircle, Trash2, Archive,
  Star, AlertTriangle, Users, Settings, LogOut, X, Folder,
  Mail, ChevronRight
} from 'lucide-react';
import { cn, getInitials } from '../lib/utils';

const folderIcons = {
  inbox: Inbox, send: Send, 'file-text': FileText,
  'alert-circle': AlertCircle, 'trash-2': Trash2,
  archive: Archive, folder: Folder
};

export const Sidebar = ({
  user, folders, labels, selectedFolder, currentView, stats,
  accountsStats = {}, onFolderSelect, onViewChange, onCompose,
  onSearch, searchQuery, isOpen, onClose
}) => {
  const { logout, accounts, activeAccount, setActiveAccount } = useAuth();

  const handleAccountClick = (acct) => {
    setActiveAccount(acct.is_default ? null : acct.email);
    onFolderSelect && onFolderSelect(null);
    onClose && onClose();
  };

  const SidebarItem = ({ icon: Icon, label, count, active, onClick, bold, testId }) => (
    <button
      onClick={onClick}
      data-testid={testId}
      className={cn(
        "w-full flex items-center gap-3 px-4 py-[7px] rounded-r-full text-[14px] transition-colors touch-manipulation text-left",
        active ? "bg-primary/20 text-primary font-semibold" : "hover:bg-accent/50 text-foreground"
      )}
    >
      {Icon && <Icon className={cn("w-[18px] h-[18px] flex-shrink-0", active ? "text-primary" : "text-muted-foreground")} />}
      <span className={cn("flex-1 truncate", count > 0 && "font-semibold")}>{label}</span>
      {count > 0 && (
        <span className={cn("text-[13px] font-semibold flex-shrink-0", active ? "text-primary" : "text-foreground")}>
          {count > 999 ? '999+' : count}
        </span>
      )}
    </button>
  );

  return (
    <>
      {isOpen && (
        <div className="md:hidden fixed inset-0 bg-black/60 z-40 backdrop-blur-sm" onClick={onClose} />
      )}

      <aside className={cn(
        "w-[260px] bg-card flex flex-col h-full border-r border-border",
        "fixed md:relative z-50 transition-transform duration-300 ease-out",
        isOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
      )}>

        {/* Header */}
        <div className="px-4 pt-4 pb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="RenoviaMail" className="w-8 h-8" />
            <span className="text-[18px] font-bold tracking-tight">RenoviaMail</span>
          </div>
          <button onClick={onClose} className="md:hidden p-1.5 hover:bg-accent rounded-full">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Compose */}
        <div className="px-3 pb-3">
          <button
            onClick={onCompose}
            className="flex items-center gap-3 px-5 py-3 rounded-2xl bg-muted hover:bg-muted/80 shadow-sm transition-all text-[14px] font-medium"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            Nouveau message
          </button>
        </div>

        <ScrollArea className="flex-1">
          <div className="py-1">

            {/* ── COMPTES ── */}
            {accounts.length > 1 && (
              <div className="mb-2">
                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-widest px-4 py-2">
                  Boîtes mail
                </p>
                {accounts.map((acct) => {
                  const isActive = (!activeAccount && acct.is_default) || activeAccount === acct.email;
                  const unread = accountsStats[acct.email]?.unread_emails || 0;
                  return (
                    <button
                      key={acct.id}
                      onClick={() => handleAccountClick(acct)}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-2 rounded-r-full transition-colors touch-manipulation",
                        isActive ? "bg-primary/20" : "hover:bg-accent/60"
                      )}
                    >
                      <Mail className={cn("w-4 h-4 flex-shrink-0", isActive ? "text-primary" : "text-foreground")} />
                      <div className="flex-1 min-w-0 text-left">
                        <p className={cn("truncate text-[13px] leading-snug", isActive ? "text-primary font-semibold" : "text-foreground font-normal")}>
                          {acct.label}
                        </p>
                        <p className="truncate text-[11px] text-muted-foreground leading-tight">{acct.email}</p>
                      </div>
                      {unread > 0 && (
                        <span className={cn("text-[13px] font-bold flex-shrink-0", isActive ? "text-primary" : "text-foreground")}>
                          {unread > 999 ? '999+' : unread}
                        </span>
                      )}
                    </button>
                  );
                })}
                <div className="border-t border-border mx-4 my-2" />
              </div>
            )}

            {/* ── DOSSIERS ── */}
            {folders.map((folder) => {
              const Icon = folderIcons[folder.icon] || Folder;
              const isActive = currentView === 'inbox' && selectedFolder?.id === folder.id;
              return (
                <SidebarItem
                  key={folder.id}
                  icon={Icon}
                  label={folder.name}
                  count={folder.unread_count}
                  active={isActive}
                  onClick={() => { onFolderSelect(folder); onClose?.(); }}
                  testId={`folder-${folder.name.toLowerCase()}`}
                />
              );
            })}

            <div className="border-t border-border mx-4 my-2" />

            {/* ── FILTRES ── */}
            <SidebarItem
              icon={Star}
              label="Favoris"
              count={stats.starred_emails}
              active={currentView === 'starred'}
              onClick={() => { onViewChange('starred'); onClose?.(); }}
              testId="filter-starred"
            />
            <SidebarItem
              icon={AlertTriangle}
              label="Important"
              count={stats.important_emails}
              active={currentView === 'important'}
              onClick={() => { onViewChange('important'); onClose?.(); }}
              testId="filter-important"
            />

            <div className="border-t border-border mx-4 my-2" />

            {/* ── NAV ── */}
            <SidebarItem
              icon={Users}
              label="Contacts"
              count={stats.total_contacts}
              active={currentView === 'contacts'}
              onClick={() => { onViewChange('contacts'); onClose?.(); }}
              testId="nav-contacts"
            />
            <SidebarItem
              icon={Settings}
              label="Paramètres"
              active={currentView === 'settings'}
              onClick={() => { onViewChange('settings'); onClose?.(); }}
              testId="nav-settings"
            />
          </div>
        </ScrollArea>

        {/* Footer */}
        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-accent/50 transition-colors">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xs font-bold flex-shrink-0">
              {getInitials(user?.full_name)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-medium truncate">{user?.full_name}</p>
              <p className="text-[11px] text-muted-foreground truncate">{user?.email}</p>
            </div>
            <button
              onClick={logout}
              className="p-1.5 hover:bg-destructive/10 hover:text-destructive rounded-lg transition-colors"
              title="Déconnexion"
            >
              <LogOut className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};
